import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { api } from '../../context/AuthContext';
import { DataTable, Loading, Section, StatusBadge, errorText, formatDateTime } from './shared';

const Field = ({ label, children }) => (
  <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[180px_1fr] sm:gap-4">
    <dt className="text-sm text-slate-500">{label}</dt>
    <dd className="min-w-0 text-sm text-slate-800">{children}</dd>
  </div>
);

/** Stripe catalog, portal and webhook state, with a manual catalog sync. */
const StripeTab = () => {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(() => api.get('/billing/admin/stripe-status').then((res) => setStatus(res.data)), []);
  useEffect(() => { load().catch(() => setStatus({})); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/billing/admin/sync-catalog');
      toast.success(res.data.created.length ? `Created: ${res.data.created.join(', ')}` : 'Catalog already up to date');
      await load();
    } catch (error) {
      toast.error(errorText(error, 'Catalog sync failed'));
    } finally {
      setSyncing(false);
    }
  };

  if (!status) return <Loading />;
  const webhook = status.webhook || {};
  const webhookStatus = webhook.status === 'enabled' && webhook.events_ok ? 'ok' : webhook.status === 'missing' ? 'gone' : 'warning';
  const prices = Object.entries(status.prices || {}).map(([key, id]) => ({ key, id }));

  return (
    <div className="space-y-6">
      <Section
        title="Stripe connection"
        description="Products and prices are created from config/plans.py on every backend start."
        actions={(
          <Button size="sm" variant="outline" onClick={sync} disabled={syncing || !status.configured}>
            {syncing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}Sync catalog
          </Button>
        )}
      >
        <dl className="divide-y divide-slate-100">
          <Field label="Mode">
            {status.configured
              ? <span className="font-medium capitalize">{status.mode}</span>
              : <span className="text-slate-500">Not configured (demo mode)</span>}
          </Field>
          <Field label="Webhook">
            <span className="flex flex-wrap items-center gap-2">
              <StatusBadge status={webhookStatus} />
              <span className="break-all text-slate-600">{webhook.url || webhook.detail || '-'}</span>
            </span>
          </Field>
          <Field label="Customer portal">{status.portal_configuration ? 'Configured' : <span className="text-slate-500">Not configured</span>}</Field>
          <Field label="Last sync">{formatDateTime(status.synced_at)}</Field>
        </dl>
      </Section>

      <Section title="Prices" description="Checkout refers to prices by lookup key; a changed amount creates a new price and moves the key.">
        <DataTable
          rowKey={(r) => r.key}
          rows={prices}
          empty="No prices synced yet"
          columns={[
            { key: 'key', label: 'Lookup key', render: (r) => <code className="text-xs">{r.key}</code> },
            { key: 'id', label: 'Stripe price', className: 'text-slate-600', render: (r) => <code className="text-xs">{r.id}</code> },
          ]}
        />
      </Section>
    </div>
  );
};

export default StripeTab;
